import {inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformServer} from '@angular/common';
import {RESPONSE} from '../../../express.tokens';
import {Response} from 'express';

/**
 * Service for managing Cloudflare cache tags in SSR responses.
 * 
 * This service allows adding and removing cache tags that will be included
 * in the Cache-Tag HTTP header when rendering on the server side.
 * These tags can be used to purge specific cached content via Cloudflare's
 * cache purging API.
 * 
 * @see https://developers.cloudflare.com/cache/how-to/purge-cache/purge-by-tags/
 */
@Injectable({
  providedIn: 'root'
})
export class CloudflareCacheTagService {
  private platformId = inject(PLATFORM_ID);
  private response = inject<Response>(RESPONSE, {optional: true});
  private tags = new Set<string>();

  /**
   * Adds a cache tag to the current response.
   * Tags are case-insensitive and automatically deduplicated.
   * Only works during server-side rendering.
   * 
   * @param tag - The cache tag to add (e.g., "product-12345")
   */
  addTag(tag: string): void {
    if (!tag || tag.trim().length === 0) {
      return;
    }

    // Only operate during SSR to avoid building up tags array in browser
    if (!isPlatformServer(this.platformId)) {
      return;
    }

    // Normalize tag: trim and convert to lowercase for consistency
    const normalizedTag = tag.trim().toLowerCase();
    
    // Add to set (automatically handles duplicates)
    this.tags.add(normalizedTag);

    // Set headers if response is available
    if (this.response) {
      this.updateResponseHeader();
    }
  }

  /**
   * Removes a cache tag from the current response.
   * Only works during server-side rendering.
   * 
   * @param tag - The cache tag to remove
   */
  removeTag(tag: string): void {
    if (!tag || tag.trim().length === 0) {
      return;
    }

    // Only operate during SSR to avoid unnecessary operations in browser
    if (!isPlatformServer(this.platformId)) {
      return;
    }

    const normalizedTag = tag.trim().toLowerCase();
    this.tags.delete(normalizedTag);

    // Update headers if response is available
    if (this.response) {
      this.updateResponseHeader();
    }
  }

  /**
   * Gets all current cache tags.
   * 
   * @returns Array of all cache tags
   */
  getTags(): string[] {
    return Array.from(this.tags);
  }

  /**
   * Clears all cache tags.
   */
  clearTags(): void {
    // Only operate during SSR to avoid unnecessary operations in browser
    if (!isPlatformServer(this.platformId)) {
      return;
    }

    this.tags.clear();
    
    if (this.response) {
      this.updateResponseHeader();
    }
  }

  /**
   * Updates the Cache-Tag header in the response with current tags.
   * Called internally after tag changes.
   */
  private updateResponseHeader(): void {
    if (!this.response) {
      return;
    }

    // Join tags with comma as per Cloudflare's specification
    const headerValue = Array.from(this.tags).join(',');
    
    if (headerValue.length > 0) {
      this.response.setHeader('Cache-Tag', headerValue);
    } else {
      // Remove header if no tags exist
      this.response.removeHeader('Cache-Tag');
    }
  }
}
